import {
    Dialog as ShadcnDialog,
    DialogClose as ShadcnDialogClose,
    DialogContent as ShadcnDialogContent,
    DialogDescription,
    DialogBody,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger as ShadcnDialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/domains/shared/services/cn";
import * as React from "react";

type DialogContextValue = {
    dismissableRef: React.MutableRefObject<boolean>;
};

const DialogContext = React.createContext<DialogContextValue | null>(null);

export function Dialog({
    open,
    onOpenChange,
    children,
}: {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    children?: React.ReactNode;
}) {
    const dismissableRef = React.useRef(true);

    return (
        <DialogContext.Provider value={{ dismissableRef }}>
            <ShadcnDialog
                open={open}
                onOpenChange={(nextOpen, details) => {
                    if (
                        !nextOpen &&
                        !dismissableRef.current &&
                        (details.reason === "escape-key" || details.reason === "outside-press")
                    ) {
                        return;
                    }
                    onOpenChange?.(nextOpen);
                }}
            >
                {children}
            </ShadcnDialog>
        </DialogContext.Provider>
    );
}

export const DialogTrigger = React.forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }
>(function DialogTrigger({ asChild, children, ...props }, ref) {
    if (asChild && React.isValidElement(children)) {
        return (
            <ShadcnDialogTrigger
                ref={ref}
                render={children as React.ReactElement}
                {...props}
            />
        );
    }

    return (
        <ShadcnDialogTrigger
            ref={ref}
            {...props}
        >
            {children}
        </ShadcnDialogTrigger>
    );
});
DialogTrigger.displayName = "DialogTrigger";

export const dialogScrollShellClassName =
    "flex max-h-[min(92vh,960px)] min-w-0 flex-col gap-0 p-0";

export const DialogContent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & { dismissable?: boolean; scrollBody?: boolean }
>(function DialogContent({ className, dismissable = true, scrollBody = false, ...props }, ref) {
    const ctx = React.useContext(DialogContext);
    if (!ctx) {
        throw new Error("DialogContent must be used within Dialog");
    }
    ctx.dismissableRef.current = dismissable;

    return (
        <ShadcnDialogContent
            ref={ref}
            showCloseButton={dismissable}
            className={cn(
                "w-[calc(100vw-2rem)] min-w-0 max-w-lg border-border bg-background shadow-lg",
                scrollBody
                    ? cn(dialogScrollShellClassName, "max-h-[min(92vh,960px)]")
                    : "max-h-[min(92vh,960px)] overflow-x-hidden overflow-y-auto p-6",
                className,
            )}
            {...props}
        />
    );
});
DialogContent.displayName = "DialogContent";

export {
    ShadcnDialogClose as DialogClose,
    DialogBody,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
};

